/**
 * A React component that provides delivery ids that can easily be copied and pasted for shipping processes
 *
 * @param {Object} props - The properties object.
 * @param {string} props.dateStart - The start date of the week to query for, formatted as 'YYYY-MM-DD'.
 * @param {string} props.dateEnd - The end date of the week to query for, formatted as 'YYYY-MM-DD'.
 *
 * @returns {JSX.Element} - A React component that contains:
 *  A button which opens a dialog that displays the delivery_ids with a simple "copy to clipboard" action
 */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import GaneshCopyToClipboardBtn from "../../../components/UI/GaneshCopyToClipboard";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";
import { DeliveryHistoryRecord_T } from "../../../types/Delivery";
import ExtraLargeDialog from "../../UI/ExtraLargeDialog";

interface ShowActiveDeliveriesBtnProps {
  dateStart: string;
  dateEnd: string;
}

const ShowActiveDeliveriesBtn: React.FC<ShowActiveDeliveriesBtnProps> = ({
  dateStart,
  dateEnd,
}) => {
  const { showError } = useError();
  const { apiRequest } = useApi();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  // Only fetch data when dialog is opened
  const {
    data: deliveryRecords,
    isLoading,
    error,
  } = useQuery<DeliveryHistoryRecord_T[]>({
    queryKey: ["active-deliveries", dateStart, dateEnd],
    queryFn: async () => {
      const params = new URLSearchParams({ dateStart, dateEnd });
      const response = await apiRequest<DeliveryHistoryRecord_T[]>(
        `/v1/deliveries/active_on_schedule?${params.toString()}`,
        { method: "GET" }
      );

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data || [];
    },
    enabled: dialogOpen,
  });

  useEffect(() => {
    if (error) {
      showError(
        `Error fetching delivery ids for ${dateStart} - ${dateEnd}: ${error.message}`
      );
    }
  }, [error, showError, dateStart, dateEnd]);

  // Memoized computed values
  const { allDeliveryIdsString, incompleteDeliveryIdsString } = useMemo(() => {
    if (!deliveryRecords) {
      return {
        allDeliveryIdsString: "",
        incompleteDeliveryIdsString: "",
      };
    }

    const allIds = deliveryRecords.map((record) => record.id).join(", ");
    const incompleteIds = deliveryRecords
      .filter((record) => !record.completed_at)
      .map((record) => record.id)
      .join(", ");

    return {
      allDeliveryIdsString: allIds,
      incompleteDeliveryIdsString: incompleteIds,
    };
  }, [deliveryRecords]);

  const handleClickOpen = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  return (
    <>
      <Button variant="contained" onClick={handleClickOpen}>
        Show Active Deliveries
      </Button>
      <ExtraLargeDialog open={dialogOpen} onClose={handleClose}>
        <DialogTitle>Delivery IDs</DialogTitle>
        <DialogContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
              >
                <GaneshCopyToClipboardBtn
                  textToCopy={allDeliveryIdsString}
                  showButtonText={true}
                  buttonText="Copy all IDs"
                  alertLabel="all IDs"
                />
              </Box>
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  padding: 2,
                  borderRadius: 2,
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #ddd",
                  mb: 2,
                }}
              >
                <h3>All Delivery IDs</h3>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    margin: 0,
                  }}
                >
                  {allDeliveryIdsString || "No IDs available"}
                </pre>
              </Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mb={2}
              >
                <GaneshCopyToClipboardBtn
                  textToCopy={incompleteDeliveryIdsString}
                  showButtonText={true}
                  buttonText="Copy incomplete IDs"
                  alertLabel="incomplete IDs"
                />
              </Box>
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  padding: 2,
                  borderRadius: 2,
                  maxHeight: "400px",
                  overflowY: "auto",
                  border: "1px solid #ddd",
                }}
              >
                <h3>Incomplete Deliveries</h3>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    margin: 0,
                  }}
                >
                  {incompleteDeliveryIdsString || "All deliveries complete 🎉"}
                </pre>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </ExtraLargeDialog>
    </>
  );
};

export default ShowActiveDeliveriesBtn;
